import type { ProviderClickAnalyticsContext } from './providerAnalytics'
import type { StorageLike } from './providerExperiment'

const TRANSITION_STORAGE_KEY = 'kvibe_provider_transition_v1'
const MAX_TRANSITION_AGE_MS = 30 * 60 * 1000

export interface PendingProviderTransition extends ProviderClickAnalyticsContext {
  clickedAt: number
  hiddenAt: number | null
}

function isPendingTransition(value: unknown, now: number): value is PendingProviderTransition {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<PendingProviderTransition>
  return (candidate.provider === 'kride' || candidate.provider === 'uber')
    && (candidate.providerPosition === 'top' || candidate.providerPosition === 'bottom')
    && (candidate.providerVariant === 'A' || candidate.providerVariant === 'B')
    && typeof candidate.sessionId === 'string'
    && typeof candidate.language === 'string'
    && typeof candidate.destinationId === 'string'
    && typeof candidate.estimatedFareBand === 'string'
    && typeof candidate.krideEnabled === 'boolean'
    && typeof candidate.uberEnabled === 'boolean'
    && typeof candidate.locationAvailable === 'boolean'
    && typeof candidate.clickedAt === 'number'
    && now - candidate.clickedAt >= 0
    && now - candidate.clickedAt <= MAX_TRANSITION_AGE_MS
    && (candidate.hiddenAt === null || typeof candidate.hiddenAt === 'number')
}

export function createPendingTransition(
  context: ProviderClickAnalyticsContext,
  now: number = Date.now(),
): PendingProviderTransition {
  return { ...context, clickedAt: now, hiddenAt: null }
}

export function readPendingTransition(
  storage: StorageLike | null,
  now: number = Date.now(),
): PendingProviderTransition | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(TRANSITION_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) as unknown : null
    if (isPendingTransition(parsed, now)) return parsed
    storage.removeItem(TRANSITION_STORAGE_KEY)
  } catch {
    // Invalid or blocked storage is treated as no pending transition.
  }
  return null
}

export function writePendingTransition(storage: StorageLike | null, transition: PendingProviderTransition): void {
  try {
    storage?.setItem(TRANSITION_STORAGE_KEY, JSON.stringify(transition))
  } catch {
    // In-memory refs still support same-page app switches.
  }
}

export function clearPendingTransition(storage: StorageLike | null): void {
  try {
    storage?.removeItem(TRANSITION_STORAGE_KEY)
  } catch {
    // Nothing else to clear.
  }
}
